import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Tier1SleeveRow } from '../../types/tier1Portfolio';

interface Props {
  sleeves: Tier1SleeveRow[];
  trackingErrorPct: number;
  cockpitAsOfLabel?: string | null;
  /** Legend for policyWeightPct bars (default Policy). */
  policySeriesName?: string;
  showCockpit?: boolean;
  showNeutral?: boolean;
  showCurrentPolicy?: boolean;
}

const Tier1AllocationChart: React.FC<Props> = ({
  sleeves,
  trackingErrorPct,
  cockpitAsOfLabel,
  policySeriesName = 'Policy',
  showCockpit: showCockpitProp,
  showNeutral: showNeutralProp,
  showCurrentPolicy = false,
}) => {
  const showCockpit =
    showCockpitProp ?? sleeves.some((s) => s.cockpitWeightPct != null);
  const showNeutral =
    showNeutralProp ?? sleeves.some((s) => s.neutralWeightPct != null);
  const showCurrent =
    showCurrentPolicy && sleeves.some((s) => s.currentPolicyWeightPct != null);

  const data = useMemo(
    () =>
      sleeves.map((s) => ({
        name: (s.name ?? s.sleeveKey).slice(0, 12),
        policy: Math.round(s.policyWeightPct * 10) / 10,
        neutral:
          s.neutralWeightPct != null
            ? Math.round(s.neutralWeightPct * 10) / 10
            : undefined,
        current:
          s.currentPolicyWeightPct != null
            ? Math.round(s.currentPolicyWeightPct * 10) / 10
            : undefined,
        cockpit:
          s.cockpitWeightPct != null
            ? Math.round(s.cockpitWeightPct * 10) / 10
            : undefined,
        actual: Math.round(s.actualWeightPct * 10) / 10,
      })),
    [sleeves],
  );

  if (data.length === 0) return null;

  return (
    <div className="h-64 w-full">
      <p className="mb-2 text-center text-xs text-gray-500">
        Sleeve weights (% of tier budget) · tracking error {trackingErrorPct.toFixed(1)}%
        {showCockpit && cockpitAsOfLabel ? ` · Cockpit ${cockpitAsOfLabel}` : ''}
      </p>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} height={56} tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 11 }} unit="%" />
          <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
          <Legend />
          {showNeutral ? (
            <Bar dataKey="neutral" name="Neutral" fill="#d1d5db" radius={[2, 2, 0, 0]} />
          ) : null}
          {showCockpit ? (
            <Bar dataKey="cockpit" name="Cockpit" fill="#a78bfa" radius={[2, 2, 0, 0]} />
          ) : null}
          {showCurrent ? (
            <Bar dataKey="current" name="Current IPS" fill="#fde68a" radius={[2, 2, 0, 0]} />
          ) : null}
          <Bar dataKey="policy" name={policySeriesName} fill="#bae6fd" radius={[2, 2, 0, 0]} />
          <Bar dataKey="actual" name="Actual" fill="#0284c7" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Tier1AllocationChart;
